import { PublicClientApplication } from '@azure/msal-browser';
import { createContext, ReactNode, useContext, useLayoutEffect, useMemo } from 'react';
import useResourceLoader from '../util/useResourceLoader';
import AuthApi, { AuthConfigDTO } from '../api/AuthApi';
import { MsalProvider } from '@azure/msal-react';
import { Trans } from '@lingui/react/macro';
import { useBootLoading } from './BootLoadingContext';

const cryptoExists = !!window.crypto.subtle;

export const AuthConfigContext = createContext<AuthConfigContextType>((undefined as unknown) as AuthConfigContextType);

export function useAuthConfig() {
  return useContext(AuthConfigContext);
}

export function AuthConfigContextFromBackendProvider({ children }: { children: ReactNode }) {
  const [authConfig, authConfigLoading = true, authConfigError] = useResourceLoader(ac => AuthApi.getAuthConfig(undefined, ac), []);
  const { show, hide } = useBootLoading();

  const value = useMemo<AuthConfigContextType | undefined>(() => {
    if (!authConfig) return undefined;
    const value: AuthConfigContextType = { typesList: [] };

    LOGINS.filter(x => x.condition(authConfig, cryptoExists)).forEach(x => {
      x.apply(authConfig, value);
      value.typesList.push(x);
    });

    value.typesList.sort((a, b) => a.order - b.order);

    return value;
  }, [authConfig, cryptoExists]);

  useLayoutEffect(() => {
    if (authConfigLoading) {
      show(<Trans>Loading application settings…</Trans>);
    } else if (!value) {
      // Terminal error-like state below — not superseded by another gate, so clear the overlay ourselves.
      hide();
    }
  }, [authConfigLoading, value]);

  if (authConfigError) throw authConfigError;
  if (authConfigLoading) return null;
  if (!value)
    return (
      <div>
        <Trans>Could not get login information for app.</Trans>
      </div>
    );

  let component: ReactNode = children;
  component = <AuthConfigContext.Provider value={value!}>{component}</AuthConfigContext.Provider>;

  if (value.azureSSO) {
    component = <MsalProvider instance={value.azureSSO.instance}>{component}</MsalProvider>;
  }

  return component;
}

const LOGINS: LoginTypeDef[] = [
  {
    type: 'Basic',
    label: __APP_CONFIG__.basicLoginLabel ?? 'Basic',
    order: __APP_CONFIG__.basicLoginOrder ?? 3,
    condition: ({ basic }) => basic,
    apply: (_, value) => {
      value.basic = true;
    },
  },
  {
    type: 'SSO',
    label: __APP_CONFIG__.ssoLoginLabel ?? 'SSO',
    order: __APP_CONFIG__.ssoLoginOrder ?? 1,
    condition: ({ azureSSO }, cryptoExists) => !!azureSSO && cryptoExists,
    apply: (cfg, value) => {
      const { clientId, authority } = cfg.azureSSO!;
      const msalConfig = {
        auth: {
          clientId,
          authority,
          redirectUri: document.location.origin,
        },
      };

      value.azureSSO = {
        clientId,
        instance: new PublicClientApplication(msalConfig),
      };
    },
  },
  {
    type: 'LDAP',
    label: __APP_CONFIG__.ldapLoginLabel ?? 'LDAP',
    order: __APP_CONFIG__.ldapLoginOrder ?? 2,
    condition: ({ ldap }) => !!ldap,
    apply: (cfg, value) => {
      const { basic, sso } = cfg.ldap!;
      value.ldap = {
        basic,
        sso,
      };
    },
  },
];

export type AuthConfigContextType = {
  basic?: boolean;

  azureSSO?: {
    clientId: string;
    instance: PublicClientApplication;
  };

  ldap?: {
    basic: boolean;
    sso: boolean;
  };

  typesList: LoginTypeDef[];
};

export type AuthType = 'SSO' | 'Basic' | 'LDAP';

export type LoginTypeDef = {
  type: AuthType;
  label: string;
  order: number;
  condition: (config: AuthConfigDTO, cryptoExists: boolean) => boolean;
  apply: (config: AuthConfigDTO, value: AuthConfigContextType) => void;
};
